import type { ServerListItem } from '@/types/fleet';

/** A server is "stale" when its last heartbeat is older than this. */
export const STALE_THRESHOLD_MS = 10 * 60_000;

/**
 * Selects the stale servers for the dashboard radiator: live servers (not
 * decommissioned, not still provisioning) whose last heartbeat is older than
 * `STALE_THRESHOLD_MS`, or which have never been seen at all.
 *
 * Pure + dependency-light so the dashboard's stale logic is unit-testable.
 */
export function selectStaleServers(
  servers: ServerListItem[],
  now: number = Date.now(),
): ServerListItem[] {
  return servers
    .filter((s) => s.status === 'active' || s.status === 'quarantined')
    .filter((s) => {
      if (!s.last_seen_at) return true;

      const seen = new Date(s.last_seen_at).getTime();

      if (Number.isNaN(seen)) return true;

      return now - seen > STALE_THRESHOLD_MS;
    })
    .sort((a, b) => {
      // Never-seen first, then oldest heartbeat first.
      const at = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const bt = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;

      return at - bt;
    });
}
